import { useCallback, useContext, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { useParams } from 'react-router-dom';
import { FormikContextType } from 'formik';
import {
  ComparisonSheetState,
  PriceSelectionItemDto,
  TenderSupplier,
} from '@Features/MaterialRequest/requests/components/ComparisonSheet/ComparisonSheet.types';
import { TenderTotalAmountResponse } from '@Types/api/form/tenderRequest/TenderTotalAmountResponse';

import { NotificationContext } from '@Context/NotificationContext';
import UnlockPricingDialog, {
  UnlockPricingFormValues,
} from '@Features/MaterialRequest/requests/components/ComparisonSheet/modals/UnlockPricingDialog/UnlockPricingDialog';

import { TenderUnlockPricingRequest } from '@Types/api/form/tenderRequest/TenderUnlockPricingRequest';
import { useUnlockTenderPricingMutation } from '@Features/MaterialRequest/requests/TenderRequest/store/api/TenderRequestApiSlice';

import { RequestListMobile } from './components';
import { SelectedFooter } from './components/FooterSection';

import styles from './selected-tab.module.css';

interface SelectedTabMobileProps {
  comparisonSheetSuppliers: TenderSupplier[];
  selectedSheetOffers: PriceSelectionItemDto[];
  totalAmountData: TenderTotalAmountResponse | undefined;
  handleReviewApprove: () => void;
  formik: FormikContextType<ComparisonSheetState>;
}

const SelectedTabMobile: React.FC<SelectedTabMobileProps> = ({
  comparisonSheetSuppliers,
  selectedSheetOffers,
  handleReviewApprove,
  totalAmountData,
  formik,
}) => {
  const { errorNotification } = useContext(NotificationContext);
  const { tenderId } = useParams();
  const [unlockPricing] = useUnlockTenderPricingMutation();
  const [showUnlock, setShowUnlock] = useState<boolean>(false);

  const handleUnlockPricing = useCallback(
    (values: UnlockPricingFormValues): void => {
      if (tenderId) {
        const time = dayjs(values.expiryTime);
        const submitDate = dayjs(values.expiryDate)
          .hour(time.hour())
          .minute(time.minute())
          .second(time.second())
          .toISOString();
        const body: TenderUnlockPricingRequest = {
          discount: values.expectedAmount || 0,
          submitDate,
          tenderId: Number(tenderId),
          remarks: values.remarks || '',
        };
        unlockPricing(body).then(() => {
          setShowUnlock(false);
        });
      }
    },
    [tenderId, unlockPricing]
  );

  const uniqSelectionNumber = useMemo(() => {
    const uniq = formik.values.selectedOffersIDs?.length
      ? Array.from(
          new Map(
            formik.values.selectedOffersIDs.map(({ matId, vendorId }) => [
              matId,
              vendorId,
            ])
          ).keys()
        ).length
      : 0;
    return uniq;
  }, [formik.values.selectedOffersIDs]);

  const isShowErrorSplit = useMemo(() => {
    const allOffers = formik.values.allOffers?.length;
    if (uniqSelectionNumber === allOffers) {
      return false;
    }
    return true;
  }, [uniqSelectionNumber, formik.values.allOffers]);

  const onReviewApprove = (): void => {
    if (isShowErrorSplit) {
      errorNotification('Please select at least one vendor for all materials');
      return;
    }
    handleReviewApprove();
  };

  return (
    <div>
      <RequestListMobile
        vendors={comparisonSheetSuppliers}
        dataSource={selectedSheetOffers}
        context="comparison-screen"
      />
      <div className={styles.footerSection}>
        <SelectedFooter
          onReviewApprove={onReviewApprove}
          totalAmount={totalAmountData?.finalTotAmount || 0}
          onSaveAsDraft={formik.handleSubmit}
          onUnlockPricing={() => {
            setShowUnlock(true);
          }}
        />
      </div>
      {showUnlock && (
        <UnlockPricingDialog
          isOpen
          initialPricing={{
            expectedAmount: 1300,
            expiryDate: '',
            expiryTime: dayjs(),
            remarks: '',
          }}
          onCancel={() => {
            setShowUnlock(false);
          }}
          onSubmit={handleUnlockPricing}
        />
      )}
    </div>
  );
};

export default SelectedTabMobile;
